// Wallet Follow API Functions
// Feature: Wallet Follow System
// API client for wallet follow operations

import { apiClient } from './client';

export interface WalletFollow {
  id: string;
  user_id: string;
  followed_address: string;
  created_at: string;
  // User info if the followed address has an account
  username?: string | null;
  avatar_url?: string | null;
  is_verified?: boolean;
}

export interface FollowResponse {
  success: boolean;
  message: string;
  follow?: WalletFollow;
  alreadyFollowing?: boolean;
}

export interface UnfollowResponse {
  success: boolean;
  message: string;
  wasFollowing: boolean;
}

export interface FollowingListResponse {
  success: boolean;
  following: WalletFollow[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface FollowerListResponse {
  success: boolean;
  address: string;
  followers: Array<{
    user_id: string;
    username: string | null;
    avatar_url: string | null;
    is_verified: boolean;
    created_at: string;
  }>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface FollowerCountResponse {
  success: boolean;
  address: string;
  followerCount: number;
}

export interface FollowStatusResponse {
  success: boolean;
  address: string;
  isFollowing: boolean;
}

/**
 * Follow a wallet address
 */
export async function followWallet(address: string): Promise<FollowResponse> {
  return apiClient.post<FollowResponse>('/wallet-follows/follow', { address });
}

/**
 * Unfollow a wallet address
 */
export async function unfollowWallet(address: string): Promise<UnfollowResponse> {
  return apiClient.post<UnfollowResponse>('/wallet-follows/unfollow', { address });
}

/**
 * Get list of wallets the authenticated user is following
 */
export async function getFollowing(
  options?: { limit?: number; offset?: number }
): Promise<FollowingListResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));

  const queryString = params.toString();
  return apiClient.get<FollowingListResponse>(
    `/wallet-follows/following${queryString ? `?${queryString}` : ''}`
  );
}

/**
 * Get followers of a specific wallet address (public)
 */
export async function getFollowers(
  address: string,
  options?: { limit?: number; offset?: number }
): Promise<FollowerListResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));

  const queryString = params.toString();
  return apiClient.get<FollowerListResponse>(
    `/wallet-follows/followers/${address}${queryString ? `?${queryString}` : ''}`
  );
}

/**
 * Get follower count for a wallet address (public)
 */
export async function getFollowerCount(address: string): Promise<FollowerCountResponse> {
  return apiClient.get<FollowerCountResponse>(`/wallet-follows/count/${address}`);
}

/**
 * Check if authenticated user is following a specific wallet
 */
export async function getFollowStatus(address: string): Promise<FollowStatusResponse> {
  return apiClient.get<FollowStatusResponse>(`/wallet-follows/status/${address}`);
}
